import React from 'react';
import type { ModuleCoverage, ModuleRegression, StorySummary } from '../../../../../../shared/types/epics';
import { StatusBadge } from '../../../../components/ui';
import { pct } from '../format';

// ---------------------------------------------------------------------------
// FORGE-275 — characterization-test safety net. Per-module coverage the
// qa-validate runner measured, with a floor line and (on regression failures)
// each module's baseline→current delta. Rendered only when the story actually
// carries safety-net data, so non-safety-net stories show nothing.
// ---------------------------------------------------------------------------

/** One module's coverage bar + lines. Below-floor modules are highlighted. */
const CoverageRow: React.FC<{ module: string; mc: ModuleCoverage; belowFloor: boolean }> = ({
  module,
  mc,
  belowFloor,
}) => {
  const width = Math.max(0, Math.min(100, mc.coverage * 100));
  return (
    <div className="py-1.5" data-testid="safety-net-module">
      <div className="flex items-center justify-between gap-2">
        <span
          className={`truncate font-mono text-xs ${belowFloor ? 'font-semibold text-rose-700' : 'text-slate-700'}`}
          title={module}
        >
          {module}
        </span>
        <span className={`shrink-0 tabular-nums text-xs ${belowFloor ? 'font-semibold text-rose-700' : 'text-slate-600'}`}>
          {pct(mc.coverage)}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-2">
        <div className="h-1.5 flex-1 overflow-hidden rounded bg-slate-100">
          <div
            className={`h-full rounded ${belowFloor ? 'bg-rose-500' : 'bg-emerald-500'}`}
            style={{ width: `${width}%` }}
          />
        </div>
        <span className="shrink-0 tabular-nums text-[11px] text-slate-400">
          {mc.linesCovered}/{mc.linesTotal} lines
        </span>
      </div>
    </div>
  );
};

export const SafetyNetPanel: React.FC<{ story: StorySummary }> = ({ story }) => {
  const coverage = story.safetyNetModuleCoverage;
  const regressions = story.safetyNetRegressions;
  const hasCoverage = coverage && Object.keys(coverage).length > 0;
  const hasRegressions = regressions && regressions.length > 0;
  // Render nothing unless there's real safety-net data on this story.
  if (!hasCoverage && !hasRegressions) return null;

  const floor = story.safetyNetCoverageFloor;
  const below = new Set(story.safetyNetBelowFloor ?? []);
  const modules = hasCoverage ? Object.keys(coverage!).sort() : [];

  return (
    <div className="mx-4 mt-3 rounded-md border border-slate-200 bg-slate-50" data-testid="safety-net-panel">
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="text-sm font-semibold text-slate-900">🛡️ Safety net</span>
        {typeof floor === 'number' && (
          <StatusBadge tone={below.size > 0 ? 'danger' : 'neutral'}>
            floor {pct(floor)}
          </StatusBadge>
        )}
        {below.size > 0 && (
          <StatusBadge tone="danger">{below.size} below floor</StatusBadge>
        )}
      </div>
      {hasCoverage && (
        <div className="border-t border-slate-200 px-3 py-1.5 divide-y divide-slate-100">
          {modules.map((m) => (
            <CoverageRow key={m} module={m} mc={coverage![m]} belowFloor={below.has(m)} />
          ))}
        </div>
      )}
      {hasRegressions && (
        <div className="border-t border-slate-200 px-3 py-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-rose-600">
            Coverage regressions
          </div>
          <ul className="mt-1 space-y-0.5">
            {regressions!.map((r: ModuleRegression) => (
              <li
                key={r.module}
                className="flex items-center justify-between gap-2 font-mono text-xs text-rose-700"
                data-testid="safety-net-regression"
              >
                <span className="truncate" title={r.module}>{r.module}</span>
                <span className="shrink-0 tabular-nums">
                  {pct(r.baseline)}→{pct(r.current)} ({pct(r.delta)})
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
